/* eslint-disable no-param-reassign */
import _ from 'underscore';
import { isShoppingCampaign, isSearchCampaign, isAudienceCampaign } from '@bingads-webui-campaign-react/constants';
import { CountryCodeToDistrictionChannelMap, listSizeConst, InMarketInternalization, DistributionChannelToCountryNameMap } from './consts';

const noData = '-';

const getReachString = ({ reach, i18n, legacyI18n }) => {
  let reachStr = noData;

  if (_.isFinite(reach) && reach >= 0) {
    // reachStr = formatReachNumber(reach);
    reachStr = reach;
  } else if (reach === -1) {
    if (i18n) {
      reachStr = i18n.getString(_TL_('Restricted'));
    } else {
      reachStr = legacyI18n.get('AudienceListSizeRestricted');
    }
  }

  return reachStr;
};

export const extendInternalizationReach = ({
  item, peopleInLocation, targetedLocationCriterions, excludedLocationCriterions, i18n, legacyI18n,
}) => {
  let reach = 0;
  let aimReach = 0;

  _.each(item.InternalizationReach.Values, (internalizationReachChannel, channelIndex) => {
    internalizationReachChannel.ListSize = [];

    //  Filter list size with target locations
    if (item.Type === 'InMarket' &&
      // locationIntentCriterion should be "People in your targeted locations"
      peopleInLocation &&
      item.AIMReach !== -1 && item.Reach !== -1) {
      let supportedMarkets;

      if (targetedLocationCriterions && targetedLocationCriterions.length > 0) {
        // there are target locations
        const targetedMarkets = _.chain(targetedLocationCriterions)
          .filter(countryCode => _.has(CountryCodeToDistrictionChannelMap, countryCode))
          .map(countryCode => CountryCodeToDistrictionChannelMap[countryCode])
          .flatten()
          .value();

        supportedMarkets = _.intersection(internalizationReachChannel.Keys, targetedMarkets);
      } else if (excludedLocationCriterions && excludedLocationCriterions.length > 0) {
        // there is no target location, but there are excluded locations
        const excludedMarkets = _.chain(excludedLocationCriterions)
          .filter(countryCode => _.has(CountryCodeToDistrictionChannelMap, countryCode))
          .map(countryCode => CountryCodeToDistrictionChannelMap[countryCode])
          .flatten()
          .value();

        supportedMarkets = _.difference(internalizationReachChannel.Keys, excludedMarkets);
      } else {
        // no target location and no excluded location
        supportedMarkets = internalizationReachChannel.Keys;
      }

      const supportedMarketsValues = [];
      let i = 0;

      _.each(internalizationReachChannel.Keys, (internalizationReachChannelKey, index) => {
        if (_.contains(supportedMarkets, internalizationReachChannelKey) &&
          // ensure each channel reach size eligible
          internalizationReachChannel.Values[index] > listSizeConst.minimumEligibleAudienceSize) {
          supportedMarketsValues.push(internalizationReachChannel.Values[index]);
          internalizationReachChannel.ListSize[i++] = getReachString({ reach: internalizationReachChannel.Values[index], i18n, legacyI18n });

          if (item.InternalizationReach.Keys[channelIndex] === 'AIM') {
            aimReach += internalizationReachChannel.Values[index];
          } else {
            reach += internalizationReachChannel.Values[index];
          }
        }
      });

      internalizationReachChannel.Keys = supportedMarkets;
      internalizationReachChannel.Values = supportedMarketsValues;

      // rewrite the total reach size.
      if (item.InternalizationReach.Keys[channelIndex] === 'AIM') {
        item.AIMReach = aimReach;
      } else {
        item.Reach = reach;
      }
    } else {
      _.each(internalizationReachChannel.Values, (internalizationReachChannelValue, index) => {
        internalizationReachChannel.ListSize[index] = getReachString({ reach: internalizationReachChannelValue, i18n, legacyI18n });
      });
    }
  });

  return item;
};

export const extendListSizeItem = ({
  item,
  hideIMAReach,
  isAIMCampaignEnabled,
  campaignType,
  i18n,
  legacyI18n, // for legacy views
  decimalUtil, // for legacy views
}) => {
  let reachData = noData;
  const errorClass = false;
  // Only hide Reach Data for IMA due to the selection
  // of multiple campaigns
  const isHideReachData = hideIMAReach && item.Type === 'InMarket';
  const getReachData = (reach) => {
    if (isHideReachData) {
      return _.isFinite(reach) && reach >= 0 ? 0 : reach;
    }
    return reach;
  };

  const searchReachData = getReachString({ reach: getReachData(item.Reach), i18n, legacyI18n });
  const aimReachData = getReachString({ reach: getReachData(item.AIMReach), i18n, legacyI18n });
  const isInEligibleIMAPreCondition = !hideIMAReach && item.Type === 'InMarket';
  let isInEligibleIMA;
  let minimumListSizeText;
  let ineligibleLocalized;
  if (i18n) {
    minimumListSizeText = i18n.getString(_TL_('< {{number}}'), {
      number: i18n.formatDecimal(listSizeConst.minimumListSize, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
    });
    ineligibleLocalized = i18n.getString(_TL_('Ineligible'));
  } else {
    minimumListSizeText = legacyI18n.getString('AudienceListSizeWarning', decimalUtil.fromFloatToString(listSizeConst.minimumListSize, 0));
    ineligibleLocalized = legacyI18n.getString('ineligible');
  }

  // Based on campaign type, decide what kinds of reach number to show
  if (isAIMCampaignEnabled) {
    if (isAudienceCampaign(campaignType)) {
      isInEligibleIMA = item.AIMReach === 0 && isInEligibleIMAPreCondition;
      reachData = isInEligibleIMA ? ineligibleLocalized : aimReachData;
    } else if (_.isNull(campaignType)) {
      isInEligibleIMA = (item.Reach === 0 && item.AIMReach === 0) && isInEligibleIMAPreCondition;
      reachData = `${item.Reach === 0 && isInEligibleIMAPreCondition ? ineligibleLocalized : searchReachData} / ${item.AIMReach === 0 && isInEligibleIMAPreCondition ? ineligibleLocalized : aimReachData}`;
    } else {
      isInEligibleIMA = item.Reach === 0 && isInEligibleIMAPreCondition;
      reachData = isInEligibleIMA ? ineligibleLocalized : searchReachData;
    }
  } else if (item.Reach >= 0) { // when isAIMCampaignEnabled is false
    if (item.Reach >= listSizeConst.minimumListSize) {
      reachData = searchReachData;
    } else {
      reachData = item.Reach === 0 && isInEligibleIMAPreCondition ? ineligibleLocalized : minimumListSizeText;
    }
  } else if (item.Reach === -1) {
    reachData = searchReachData;
  }

  return _.extend(item, {
    listSize: reachData, listSizeSearch: searchReachData, listSizeAudience: aimReachData, errorClass, isInEligibleIMA,
  });
};

export const getLocalizedMarkets= ({
  item,
  i18n,
  legacyI18n,
  campaignType,
  selectedChannel,
}) => {
  const searchNetworkIndex = _.indexOf(item.InternalizationReach.Keys, InMarketInternalization.SearchNetwork);
  const audienceNetworkIndex = _.indexOf(item.InternalizationReach.Keys, InMarketInternalization.AudienceNetwork);

  const isSearchCampaignType = isSearchCampaign(campaignType) || isShoppingCampaign(campaignType);

  // for bulk edit scenario, we show audience and search channel for single audience sepearately
  const isSearchChannel = selectedChannel === InMarketInternalization.SearchChannel;

  // only show corresponding markets (markets availble in current channel)
  const marketList = isSearchCampaignType || isSearchChannel ?
    item.InternalizationReach.Values[searchNetworkIndex].Keys :
    item.InternalizationReach.Values[audienceNetworkIndex].Keys;
  const marketReachMap = _.map(item.InternalizationReach.Values, network => _.object(network.Keys, network.ListSize));
  const marketNetworkMap = {};

  marketList.forEach((market) => {
    marketNetworkMap[market] = {
      searchReach: searchNetworkIndex < 0 || !(market in marketReachMap[searchNetworkIndex]) ?
        '-' : marketReachMap[searchNetworkIndex][market],
      audienceReach: audienceNetworkIndex < 0 || !(market in marketReachMap[audienceNetworkIndex]) ?
        '-' : marketReachMap[audienceNetworkIndex][market],
    };
  });

  return marketList.map((marketName) => {
    if (i18n) {
      return i18n.getString(DistributionChannelToCountryNameMap[`DistributionChannelId_${marketName}`]);
    }
    return legacyI18n.get(`DistributionChannelId_${marketName}`);
  });
};

export const formatReachEstimation = (reachEstimation, i18n) => {
  if (reachEstimation === undefined || reachEstimation === null || reachEstimation < 0) {
    return '-';
  }

  if (reachEstimation >= 1000) {
    return i18n.formatDecimal(reachEstimation, {
      compact: 'short',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return i18n.formatDecimal(reachEstimation, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};
